package com.milta.feature.route.domain.entity

import com.milta.core.network.ApiException

/** One delivery the server refused at creation: [index] is its position in the `deliveries` array
 * sent (`POST /routes`, `/routes/me`, `/route-batches`), [reason] the HERE motif. */
data class AddressToFix(val index: Int, val reason: GeocodeFailureReason)

/**
 * 422 `error.route.addresses_to_fix` — the server checked the addresses again (HERE) and refused
 * some. [deliveries] is null when the back doesn't report them (older back): the screens then never
 * say "à corriger" over an empty list, only a generic "réessayez".
 */
class AddressesToFixException(val deliveries: List<AddressToFix>?) : ApiException(HTTP_UNPROCESSABLE_ENTITY, CODE) {
    companion object {
        const val CODE = "error.route.addresses_to_fix"
        private const val HTTP_UNPROCESSABLE_ENTITY = 422
    }
}

/**
 * Marks the items the server refused (by their position in the list sent) with their motif. Null
 * when none of [toFix] points to an item of this list: nothing to show in "À corriger".
 */
fun <T> List<T>.markAddressesToFix(toFix: List<AddressToFix>?, mark: T.(GeocodeFailureReason) -> T): List<T>? {
    val reasons = toFix.orEmpty().filter { it.index in indices }.associate { it.index to it.reason }
    if (reasons.isEmpty()) return null
    return mapIndexed { i, item -> reasons[i]?.let { item.mark(it) } ?: item }
}

/** A draft refused by the server: Failed with its motif, so it shows in "À corriger" until edited. */
fun DeliveryDraft.markedToFix(reason: GeocodeFailureReason): DeliveryDraft =
    copy(geocodeStatus = GeocodeStatus.Failed, geocodeFailureReason = reason)
